import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface Props {
	/** The minted one-time token, or null when the dialog is closed. */
	token: string | null;
	keyName?: string;
	onClose: () => void;
}

/**
 * Shows a freshly-minted/regenerated MCP key token exactly once (only its hash is stored
 * server-side). Includes the MCP-client connection hint — the header an agent (e.g. Claude
 * Code) must present on /mcp (docs/ai/02b §4.3).
 */
export function MintedTokenDialog({ token, keyName, onClose }: Props) {
	const { t } = useTranslation();
	const [copied, setCopied] = useState(false);

	const copy = async () => {
		if (!token) return;
		try {
			await navigator.clipboard.writeText(token);
			setCopied(true);
			toast.success(t('system.mcpKeys.copied'));
			setTimeout(() => setCopied(false), 2000);
		} catch {
			toast.error(t('system.mcpKeys.copyFailed'));
		}
	};

	return (
		<Dialog open={Boolean(token)} onOpenChange={open => !open && onClose()}>
			<DialogContent showCloseButton={false}>
				<DialogHeader>
					<DialogTitle>{t('system.mcpKeys.secretTitle', { name: keyName ?? '' })}</DialogTitle>
					<DialogDescription>{t('system.mcpKeys.secretSubtitle')}</DialogDescription>
				</DialogHeader>

				<div className="flex items-start gap-2 rounded-md border border-warning bg-warning/10 px-3 py-2 text-xs text-warning-fg">
					<AlertTriangle size={15} className="mt-0.5 shrink-0" />
					<span>{t('system.mcpKeys.secretWarning')}</span>
				</div>

				<div className="flex items-center gap-2 rounded-md border border-border bg-muted px-3 py-2">
					<code className="flex-1 break-all font-mono text-xs text-foreground">{token}</code>
					<Button type="button" variant="outline" size="icon" onClick={copy} aria-label={t('system.mcpKeys.copy')}>
						{copied ? <Check size={15} className="text-success-fg" /> : <Copy size={15} />}
					</Button>
				</div>

				<p className="text-xs text-foreground-muted">
					{t('system.mcpKeys.connectHint')}{' '}
					<code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">Authorization: MCP &lt;token&gt;</code>
				</p>

				<DialogFooter>
					<Button type="button" onClick={onClose}>
						{t('system.mcpKeys.secretDone')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
